import React, { useCallback, useMemo, useRef } from "react";

import cn from "classnames";

import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import User from "@mapmycustomers/shared/types/User";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import ColumnVisibility from "@app/types/viewModel/ColumnVisibility";
import Fetcher from "@app/types/viewModel/Fetcher";

import IViewContext, { ViewStateUpdater } from "../types/IViewContext";
import ViewComponent from "../types/ViewComponent";
import ViewDataFetcher from "../types/ViewDataFetcher";
import mergeFilterModels from "../utils/mergeFilterModels";
import ViewContext from "../utils/ViewContext";

import styles from "./GenericView.module.scss";

interface Props<T extends AnyEntity, ResponseType = ListResponse<T>> {
  children: React.ReactNode;
  className?: string;
  entityType?: EntityType;
  fetcher: Fetcher<T, ResponseType>;
  fieldModel: IFieldModel;
  totalRecords?: number;
  viewState: ViewState;
}

const GenericView = <T extends AnyEntity, ResponseType = ListResponse<T>>({
  children,
  className,
  entityType,
  fetcher,
  fieldModel,
  totalRecords,
  viewState,
}: Props<T, ResponseType>) => {
  const components = useRef<Set<ViewComponent<T>>>(new Set());

  const handleUpdateViewState: ViewStateUpdater = useCallback(
    (viewState: Partial<ViewState>) => {
      fetcher({ updateOnly: true, viewState });
      Array.from(components.current).forEach((callback) => {
        callback.onViewStateChange?.(viewState);
      });
    },
    [fetcher]
  );

  const handleUpdateViewStateAndFetch: ViewDataFetcher<ResponseType> = useCallback(
    (
      viewState: Partial<ViewState>,
      dataCallback?: (response: ResponseType) => void,
      failCallback?: () => void
    ) => fetcher({ dataCallback, failCallback, updateOnly: false, viewState }),
    [fetcher]
  );

  const handleUpdateFilters: IViewContext["updateFilterModel"] = useDynamicCallback(
    (newFilterModel: Partial<FilterModel>) => {
      const resultingFilterModel = mergeFilterModels(viewState.filter, newFilterModel);
      handleUpdateViewState({ filter: resultingFilterModel });
      Array.from(components.current).forEach((callback) => {
        callback.onFilterChange?.(newFilterModel);
      });
    }
  );

  const handleColumnVisibilityChange: IViewContext["updateColumnVisibility"] = useDynamicCallback(
    (columnVisibilities: ColumnVisibility[]) => {
      const visibilityMap = new Map(
        columnVisibilities.map(({ field, visible }) => [field.name, visible])
      );
      const columns = viewState.columns.map((column) => ({
        ...column,
        visible: !!visibilityMap.get(column.field.name),
      }));
      handleUpdateViewState({ columns });
      Array.from(components.current).forEach((callback) => {
        callback.onColumnVisibilityChange?.(columnVisibilities);
      });
    }
  );

  const handleUpdateEntity: IViewContext["updateEntity"] = useDynamicCallback((entity: T) => {
    Array.from(components.current).forEach((callback) => {
      callback.onUpdateEntity?.(entity);
    });
  });

  const handleUpdateViewAs: IViewContext["updateViewAs"] = useDynamicCallback(
    (userId: undefined | User["id"]) => {
      handleUpdateViewState({ viewAs: userId });
      Array.from(components.current).forEach((callback) => {
        callback.onViewAsChange?.(userId);
      });
    }
  );

  const handleRegister = useCallback(
    (component: ViewComponent<T>) => components.current.add(component),
    [components]
  );

  const handleUnregister = useCallback(
    (component: ViewComponent<T>) => components.current.delete(component),
    [components]
  );

  const context: IViewContext<T, ResponseType> = useMemo(
    () => ({
      entityType,
      fieldModel,
      register: handleRegister,
      totalRecords,
      unregister: handleUnregister,
      updateColumnVisibility: handleColumnVisibilityChange,
      updateEntity: handleUpdateEntity,
      updateFilterModel: handleUpdateFilters,
      updateViewAs: handleUpdateViewAs,
      updateViewState: handleUpdateViewState,
      updateViewStateAndFetch: handleUpdateViewStateAndFetch,
      viewState,
    }),
    [
      entityType,
      fieldModel,
      handleUpdateFilters,
      handleColumnVisibilityChange,
      handleUpdateEntity,
      handleUpdateViewAs,
      handleRegister,
      handleUnregister,
      handleUpdateViewState,
      handleUpdateViewStateAndFetch,
      viewState,
      totalRecords,
    ]
  );

  return (
    <section className={cn(styles.container, className)}>
      <ViewContext.Provider
        // Ignored because TS can't be sure that the Provider was created for exactly same
        // entity type as T. Although we know that it will be.
        // @ts-ignore
        value={context}
      >
        {children}
      </ViewContext.Provider>
    </section>
  );
};

export default GenericView;
